//@ts-strict-ignore
/*
 * Attempting to extract this logic as a hook due to complexity of ConnectedForm making it difficult to work with.
 * Only intended/expected use is within connectedForm, not elsewhere.
 * */
import { useHistory, useLocation } from 'react-router';
import { useCallback, useContext, useMemo } from 'react';
import ModalContext from '../Context/ModalContext';
import { Analytics } from '../../mixpanel';
import { IAbstractEntity } from '../../graphql/schema/p4types';
import { EntityType } from '../../@types/entityTypes';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { LocationDescriptorObject } from 'history';

type IAfterSubmitInput = {
  result?: IAbstractEntity & { [propName: string]: any };
};
type IConnectedFormBackNavigationOptions = {
  defaultReturnUrl?: string;
  entity?: EntityType;
  location: any;
  modalClose?: boolean;
  onClose?: () => void;
  submitOptions: [submitState: any]; //incomplete, there are additional arguments.
  reloadData: (opt1: boolean, options?: { getDefault?: boolean }) => Promise<any>;
};
export const useConnectedFormBackNavigation = ({
  defaultReturnUrl,
  entity,
  location: locationFromProps,
  modalClose,
  onClose,
  submitOptions,
  reloadData,
}: IConnectedFormBackNavigationOptions) => {
  const history = useHistory<{ previousLocation: string; modal?: boolean }>();
  const locationFromHook = useLocation();
  const location = useMemo(() => locationFromProps ?? locationFromHook, [locationFromHook, locationFromProps]);
  const modalContext = useContext(ModalContext);
  const formContext = useContext(ConnectedFormContext);
  const navigateBack = (result: any) => {
    if (modalClose && (onClose || modalContext?.onClose)) {
      (onClose || modalContext?.onClose)?.(result);
      return;
    }
    const returnUrl = location?.state?.returnUrl;
    const previousUrl = location?.state?.previousLocation;
    if (returnUrl) {
      //Return URL defined by location.state - Follow it.
      if (returnUrl === 'close') {
        window.close();
      } else if (returnUrl !== 'back') {
        history.push({
          pathname: returnUrl,
          state: { previousLocation: history.location?.pathname || 'back' },
        });
      }
    } else if (previousUrl) {
      //We have a previous URL so goBack should be within P4.
      history.goBack();
    } else if (defaultReturnUrl || entity?.workspaceUrl) {
      //If no default return url specified, use the workspace URL.
      //No return URL defined & no history - Go to default return URL (E.g. Workspace).
      history.push({
        pathname: defaultReturnUrl || entity?.workspaceUrl,
        state: { previousLocation: history.location?.pathname || 'back' },
      });
    } else {
      //No default return location + No history to return to - Throw out a console warning as this should be fixed.
      console.warn('Unable to go back - Default return URL not defined');
    }
  };
  //postSubmit would be a better name, but don't want to confuse 'post' options on forms with this functionality.
  //(Or maybe 'afterSubmitSuccess', since we should only process this if no errors occurred).
  //Renamed from 'navigateBack' due to new Save & Continue/Save & New options.
  const afterSubmit = ({ result }: IAfterSubmitInput = {}) => {
    const [submitState] = submitOptions;
    Analytics.track('Form saved', { type: submitState?.value });
    //Continue - Just return.
    if (submitState?.value === 'CONTINUE') {
      // If formContext has values, then we must be in a "Form within a Form" - e.g. Raising a PO from an SO
      // If this is the case, don't reload the form by navigating away
      if (!formContext.values && !modalClose) {
        const newLocation: LocationDescriptorObject<{ previousLocation: string; modal?: boolean }> = {
          pathname: history.location?.pathname,
        };
        if (history.location?.state) {
          //Maintain 'modal' state so workspace stays mounted in background.
          newLocation.state = {
            previousLocation: history.location.state.previousLocation,
            modal: history.location.state.modal,
          };
        }
        if (newLocation.pathname?.includes('/New') && result?.id) {
          newLocation.pathname = newLocation.pathname?.replace('/New', `/${result.id}`);
          history.replace(newLocation);
        }
      }
      return;
    }
    //New - Reload defaults to create another new record.
    if (submitState?.value === 'NEW') {
      reloadData(true, { getDefault: true }).then(() => {
        //Do Nothing (Promise returns data, but we have no need to process it here).
      });
      return;
    }
    //Save/Exit - Otherwise, process 'navigateBack' behaviour as before.
    navigateBack(result);
  };
  return { navigateBack, afterSubmit };
};

/**
 * Subset of above for use on "react-hook-form" forms (I.e. Stock Take).
 * Expect implementing form to deal with "Modal" back navigation since submit is handled per-implementation rather than generically by the form.
 */
export const useP4BackNavigation = (entity: EntityType) => {
  const history = useHistory<{ previousLocation: string; modal?: boolean }>();
  const location = useLocation<{ returnUrl?: string; previousLocation?: string }>();

  return useCallback(() => {
    const returnUrl = location?.state?.returnUrl;
    const previousUrl = location?.state?.previousLocation;
    if (returnUrl) {
      //Return URL defined by location.state - Follow it.
      if (returnUrl === 'close') {
        window.close();
      } else if (returnUrl !== 'back') {
        history.push({
          pathname: returnUrl,
          state: { previousLocation: history.location?.pathname || 'back' },
        });
      }
    } else if (previousUrl) {
      //We have a previous URL so goBack should be within P4.
      history.goBack();
    } else if (entity?.workspaceUrl) {
      //If no default return url specified, use the workspace URL.
      //No return URL defined & no history - Go to default return URL (E.g. Workspace).
      history.push({
        pathname: entity?.workspaceUrl,
        state: { previousLocation: history.location?.pathname || 'back' },
      });
    } else {
      //No default return location + No history to return to - Throw out a console warning as this should be fixed.
      console.warn('Unable to go back - Default return URL not defined');
    }
  }, []);
};
